import React, { useState, useRef, useEffect, useCallback } from "react";
import TextArea from "antd/es/input/TextArea";
import { EditOutlined, CopyOutlined, DeleteOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { TagView } from "../../../assets/utilities/TagView";
import { copyToClipboard } from "../../../assets/utilities/PageUtils";
import { useThemeColors } from "redux/hooks";
import { notification } from "antd";
import { ConditionalTooltip } from "./ConditionalTooltip";

export function QuestionSectionView(props: {
    question: string;
    isEditMode: boolean;
    isEditing: boolean;
    darkMode: boolean;
    tagViewModel: any;
    tagList: string[];
    onEdit: () => void;
    onEditSubmit: (newText: string) => void;
    onEditCancel: () => void;
    onDelete: () => void;
    registerQuestionRef: (node: HTMLDivElement | null) => void;
    jobWrapperRef: React.RefObject<HTMLDivElement | null>;
    isResponseAnimating?: boolean;
}) {
    const { question, isEditMode, isEditing, darkMode, tagViewModel, tagList, onEdit, onEditSubmit, onEditCancel, onDelete, registerQuestionRef, jobWrapperRef, isResponseAnimating = false } = props;
    const themeColors = useThemeColors();
    const [editQuestionText, setEditQuestionText] = useState("");
    const [isEditHovered, setIsEditHovered] = useState(false);
    const [isCopyQuestionHovered, setIsCopyQuestionHovered] = useState(false);
    const [isDeleteHovered, setIsDeleteHovered] = useState(false);
    const [pressedQuestion, setPressedQuestion] = useState(false);
    const [pressedDelete, setPressedDelete] = useState(false);
    const editInputRef = useRef<HTMLDivElement>(null);
    const editTextAreaRef = useRef<any>(null);

    useEffect(() => {
        if (isEditMode) {
            setEditQuestionText(question);
            setTimeout(() => {
                jobWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const textArea = editTextAreaRef.current?.resizableTextArea?.textArea;
                if (textArea) {
                    textArea.focus();
                    textArea.setSelectionRange(textArea.value.length, textArea.value.length);
                }
            }, 100);
        }
    }, [isEditMode, question, jobWrapperRef]);

    useEffect(() => {
        if (!isEditMode) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (editInputRef.current && !editInputRef.current.contains(event.target as Node)) {
                onEditCancel();
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 200);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditMode, onEditCancel]);

    const handleEditSubmit = useCallback(() => {
        if (!editQuestionText.trim() || editQuestionText.trim() === question) {
            onEditCancel();
            return;
        }
        onEditSubmit(editQuestionText.trim());
    }, [editQuestionText, question, onEditSubmit, onEditCancel]);

    return (
        <div
            style={{
                alignSelf: isEditMode ? "stretch" : "flex-end",
                display: "flex",
                flexDirection: "column",
                alignItems: isEditMode ? "stretch" : "flex-end",
                marginBottom: "1em",
                maxWidth: isEditMode ? "100%" : "95%",
                width: isEditMode ? "100%" : undefined,
                marginLeft: isEditMode ? undefined : "auto"
            }}
        >
            {!isEditMode ? (
                <div
                    ref={(node) => registerQuestionRef(node)}
                    style={{
                        backgroundColor: themeColors.primaryColor,
                        color: "white",
                        padding: "0.5em 0.75em",
                        borderRadius: "1em 1em 0.2em 1em",
                        maxWidth: "50%",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word"
                    }}
                >
                    {question}
                </div>
            ) : (
                <div
                    ref={editInputRef}
                    style={{
                        width: "100%",
                        position: "relative"
                    }}
                >
                    <TextArea
                        ref={editTextAreaRef}
                        value={editQuestionText}
                        onChange={(e) => setEditQuestionText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && editQuestionText.trim() && editQuestionText.trim() !== question) {
                                e.preventDefault();
                                handleEditSubmit();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onEditCancel();
                            }
                        }}
                        autoSize={{ minRows: 1, maxRows: 10 }}
                        style={{
                            width: "100%",
                            fontSize: "1em",
                            lineHeight: "1.4em",
                            border: darkMode ? "1px solid #404040" : "1px solid #d9d9d9",
                            borderRadius: "8px",
                            padding: "0.5em 2.8em 0.5em 0.5em",
                            resize: "none",
                            backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                            color: darkMode ? "#ffffff" : "#000000"
                        }}
                    />
                    <ConditionalTooltip title="Submit (Enter)">
                        <button
                            onClick={handleEditSubmit}
                            disabled={isResponseAnimating || !editQuestionText.trim() || editQuestionText.trim() === question}
                            style={{
                                position: "absolute",
                                bottom: "0.4em",
                                right: "0.2em",
                                width: "2.2em",
                                height: "2.2em",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: (!isResponseAnimating && editQuestionText.trim() && editQuestionText.trim() !== question)
                                    ? (darkMode ? "#4A9EFF" : "#1E6692")
                                    : (darkMode ? "#404040" : "#d9d9d9"),
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: (!isResponseAnimating && editQuestionText.trim() && editQuestionText.trim() !== question) ? "pointer" : "not-allowed",
                                transition: "background-color 0.2s"
                            }}
                        >
                            <ArrowUpOutlined style={{ fontSize: "0.9em" }} />
                        </button>
                    </ConditionalTooltip>
                </div>
            )}
            <div style={{ marginTop: "0.25em", display: "flex", gap: "0.75em", alignSelf: "flex-end" }}>
                <TagView model={tagViewModel} tagList={tagList} />
                <ConditionalTooltip title="Edit request">
                    <button
                        onClick={onEdit}
                        disabled={isEditing || isEditMode}
                        onMouseEnter={() => setIsEditHovered(true)}
                        onMouseLeave={() => setIsEditHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: (isEditing || isEditMode) ? "not-allowed" : "pointer",
                            fontSize: "20px",
                            color: (isEditing || isEditMode)
                                ? (darkMode ? "#666666" : "#CCCCCC")
                                : (isEditHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF")),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s"
                        }}
                    >
                        <EditOutlined />
                    </button>
                </ConditionalTooltip>
                <ConditionalTooltip title="Copy request">
                    <button
                        onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(question.trim());
                                setPressedQuestion(true);
                                setTimeout(() => setPressedQuestion(false), 200);
                                notification.success({
                                    message: 'Copied to clipboard',
                                    description: 'Request has been copied to your clipboard.',
                                    placement: 'bottomLeft',
                                    duration: 2
                                });
                            } catch (error) {
                                notification.error({
                                    message: 'Failed to copy',
                                    description: 'Could not copy to clipboard. Please try again.',
                                    placement: 'bottomLeft',
                                    duration: 3
                                });
                            }
                        }}
                        onMouseEnter={() => setIsCopyQuestionHovered(true)}
                        onMouseLeave={() => setIsCopyQuestionHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: isCopyQuestionHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: pressedQuestion ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <CopyOutlined />
                    </button>
                </ConditionalTooltip>
                <ConditionalTooltip title="Delete this request">
                    <button
                        onClick={() => {
                            setPressedDelete(true);
                            setTimeout(() => setPressedDelete(false), 200);
                            onDelete();
                        }}
                        onMouseEnter={() => setIsDeleteHovered(true)}
                        onMouseLeave={() => setIsDeleteHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: isDeleteHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: pressedDelete ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <DeleteOutlined />
                    </button>
                </ConditionalTooltip>
            </div>
        </div>
    );
};

